// pages/ProductDetail.jsx
import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Minus, ArrowLeft } from 'lucide-react';
import CartModal from '../components/CartModal';
import ImagePopup from '../components/ImagePopup';
import { useCart } from '../context/CartContext';
import { ProductContext } from '../context/ProductContext';

const ProductDetail = () => {
  const { products, setProducts } = useContext(ProductContext);
  const { addToCart, cartMessage } = useCart(); // Add cartMessage here
  const { cart } = useCart();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [quantity, setQuantity] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  const getImageUrl = (imagePath) => {
    const baseURl = import.meta.env.VITE_API_URL;
    return `${baseURl}/api/v1/${imagePath}`;
  };

  const product = products.find(p => p.fashion_id === parseInt(id));
  if (!product) return null;

  const variations = product.data;
  const uniqueColors = [...new Set(variations.map(v => v.color_name))];
  const uniqueSizes = [...new Set(variations.map(v => v.size_name))];

  // Product structured data for SEO
  const renderSEOMetadata = () => {
    const baseUrl = import.meta.env.VITE_API_URL;
    const productImage = getImageUrl(product.image_url);
    const fullUrl = window.location.href;
    const currentPath = window.location.pathname;
    const isProductDetailPath = currentPath.includes('/product/');
    
    // Create breadcrumb data
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": window.location.origin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Products",
          "item": `${window.location.origin}/${location.state?.selectedCategory || "All Products"}`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": product.name,
          "item": fullUrl
        }
      ]
    };

    // Create product structured data
    const productData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "image": productImage,
      "description": product.description || product.name,
      "sku": `STOK-${product.fashion_id}`,
      "mpn": product.fashion_id.toString(),
      "brand": {
        "@type": "Brand",
        "name": "STOK"
      },
      "offers": {
        "@type": "Offer",
        "url": fullUrl,
        "price": product.price,
        "priceCurrency": product.price_ccy || "USD",
        "availability": variations.some(v => v.quantity > 0) ? 
          "https://schema.org/InStock" : 
          "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    };

    return (
      <Helmet>
        <title>{`${product.name} | STOK`}</title>
        <meta name="description" content={product.description || `${product.name} - Shop now at STOK`} />
        <meta name="keywords" content={`${product.name}, ${product.quality || ''}, online shopping, STOK`} />
        
        {/* Only add Open Graph tags for product detail pages */}
        {isProductDetailPath && (
          <>
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="product" />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:title" content={`${product.name} | STOK`} />
            <meta property="og:description" content={product.description || `${product.name} - Shop now at STOK`} />
            <meta property="og:image" content={productImage} />
            <meta property="product:price:amount" content={product.price.toString()} />
            <meta property="product:price:currency" content={product.price_ccy || "USD"} />
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={fullUrl} />
            <meta property="twitter:title" content={`${product.name} | STOK`} />
            <meta property="twitter:description" content={product.description || `${product.name} - Shop now at STOK`} />
            <meta property="twitter:image" content={productImage} />
          </>
        )}
        
        {/* Structured data for product */}
        <script type="application/ld+json">
          {JSON.stringify(productData)}
        </script>
        
        {/* Structured data for breadcrumbs */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      </Helmet>
    );
  };

  // Move handleAddToCart inside component
  const handleAddToCart = async (product, variation) => {
    if (!variation) return;

    setAddingToCart(true);

    const cartItem = {
      id: product.fashion_id,
      name: product.name,
      price: product.price,        // 21000
      price_ccy: product.price_ccy, // "KHR"
      size: variation.size_name,
      color: variation.color_name,
      quantity: quantity,
      image: product.image_url
    };

    // Simulate network delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));

    addToCart(cartItem, products, setProducts);
    setAddingToCart(false);

    // Navigate back with the category
    const returnCategory = location.state?.selectedCategory || "All Products";
    navigate(-1, { state: { selectedCategory: returnCategory } });
  };

  // Add this function to calculate current available quantity
  const getCurrentAvailableQuantity = (variation) => {
    if (!variation) return 0;

    const cartItem = cart.find(item =>
      item.id === parseInt(id) &&
      item.color === variation.color_name &&
      item.size === variation.size_name
    );

    return variation.quantity - (cartItem?.quantity || 0);
  };

  const getAvailableVariation = (color, size) => {
    return variations.find(v =>
      v.color_name === color &&
      v.size_name === size &&
      getCurrentAvailableQuantity(v) > 0
    );
  };

  // Add this helper function to check if a color has any available items
  const hasAvailableItems = (color) => {
    return variations.some(v =>
      v.color_name === color &&
      getCurrentAvailableQuantity(v) > 0
    );
  };

  return (
    <div className="mx-auto px-4 py-8 max-w-7xl">
      {renderSEOMetadata()}
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Products</span>
      </button>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-3">
          <div
            className="aspect-square w-full cursor-pointer relative group"
            onClick={() => {
              setSelectedImage(product.image_url);
              setShowImagePopup(true);
            }}
          >
            <img
              src={getImageUrl(product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover rounded-lg"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg" />
          </div>
          <p className="text-sm text-gray-500">{product.description}</p>
        </div>

        <div className="space-y-6">
          {/* Color Selection */}
          <div className="space-y-3">
            <h3 className="font-medium">Color</h3>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {uniqueColors.map(color => {
                const isSelected = selectedVariation?.color_name === color;
                const isAvailable = hasAvailableItems(color);

                return (
                  <button
                    key={color}
                    onClick={() => {
                      if (!isAvailable) return;
                      // Find first available variation with this color
                      const newVariation = variations.find(v =>
                        v.color_name === color &&
                        getCurrentAvailableQuantity(v) > 0
                      );
                      if (newVariation) {
                        const availableQty = getCurrentAvailableQuantity(newVariation);
                        if (quantity > availableQty) {
                          setQuantity(1);
                        }
                        setSelectedVariation(newVariation);
                      }
                    }}
                    disabled={!isAvailable}
                    className={`
              p-3 rounded-lg text-sm font-medium
              ${isSelected
                        ? 'bg-gray-900 text-white'
                        : isAvailable
                          ? 'bg-white border hover:border-gray-400'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }
            `}
                  >
                    {color}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Size Selection */}
          <div className="space-y-3">
            <h3 className="font-medium">Size</h3>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6">
              {uniqueSizes.map(size => {
                const variation = selectedVariation?.color_name
                  ? getAvailableVariation(selectedVariation.color_name, size)
                  : null;

                const isAvailable = !!variation;
                const isSelected = selectedVariation?.size_name === size;

                return (
                  <button
                    key={size}
                    onClick={() => {
                      if (!selectedVariation?.color_name || !isAvailable) return;

                      // Reset quantity if it exceeds new variation's available stock
                      const availableQty = getCurrentAvailableQuantity(variation);
                      if (quantity > availableQty) {
                        setQuantity(1);
                      }
                      setSelectedVariation(variation);
                    }}
                    disabled={!selectedVariation?.color_name || !isAvailable}
                    className={`
              p-3 rounded-lg text-sm font-medium
              ${isSelected
                        ? 'bg-gray-900 text-white'
                        : isAvailable
                          ? 'bg-white border hover:border-gray-400'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }
            `}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity Selector (only if variation is selected) */}
          {selectedVariation && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Quantity:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(getCurrentAvailableQuantity(selectedVariation), quantity + 1))}
                  disabled={quantity >= getCurrentAvailableQuantity(selectedVariation)}
                  className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-500">
                  ({getCurrentAvailableQuantity(selectedVariation)} available)
                </span>
              </div>
            </div>
          )}

          {/* Add to Cart Button */}
          <button
            onClick={() => handleAddToCart(product, selectedVariation)}
            disabled={!selectedVariation || addingToCart}
            className={`
      w-full py-3 sm:py-3.5 rounded-xl text-base font-medium transition-all
      ${selectedVariation
                ? addingToCart
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
    `}
          >
            {addingToCart ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding to Cart...
              </span>
            ) : (
              'Add to Cart'
            )}
          </button>
        </div>
      </div>

      <CartModal
        showCart={showCart}
        setShowCart={setShowCart}
        getImageUrl={getImageUrl}
      />

      <ImagePopup
        showImagePopup={showImagePopup}
        setShowImagePopup={setShowImagePopup}
        selectedImage={selectedImage}
        getImageUrl={getImageUrl}
      />

      {cartMessage.text && (
        <div className={`
                    fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-opacity duration-300
                    ${cartMessage.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
                `}>
          {cartMessage.text}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;